import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Icon } from '@iconify/react';
import CONFIG from '../../config';
import Swal from 'sweetalert2';
import LoadingSpinner from '../common/LoadingSpinner';

const LiveRun = ({ project, onBack }) => {
    const [liveData, setLiveData] = useState({ run_start_time: null, candidates: [] });
    const [initialLoading, setInitialLoading] = useState(true);
    const [elapsedTime, setElapsedTime] = useState('00:00.0');
    const [anomalies, setAnomalies] = useState([
        { id: 1, type: 'warning', message: 'RFID Tag #442 potential signal skip at Mat 2', time: '12:04:10' },
        { id: 2, type: 'info', message: 'Batch synchronization successful.', time: '12:04:05' }
    ]);

    // Refs — always up-to-date, never stale in intervals
    const runStartTimeRef = useRef(null);
    const isRaceActiveRef = useRef(false);
    const timerRef = useRef(null);
    const dataIntervalRef = useRef(null);
    // Track previously-active state to detect auto-stop
    const prevCandidateCountRef = useRef(null);

    useEffect(() => {
        fetchLiveData();
        dataIntervalRef.current = setInterval(fetchLiveData, 2000);
        return () => {
            clearInterval(dataIntervalRef.current);
            clearInterval(timerRef.current);
        };
    }, [project]);

    // React to race start/stop driven by run_start_time
    useEffect(() => {
        runStartTimeRef.current = liveData.run_start_time;

        if (liveData.run_start_time && !isRaceActiveRef.current) {
            // Race just became active — start timer
            isRaceActiveRef.current = true;
            clearInterval(timerRef.current);
            timerRef.current = setInterval(updateTimer, 100);
        } else if (!liveData.run_start_time && isRaceActiveRef.current) {
            // Race was stopped externally — clear timer
            isRaceActiveRef.current = false;
            clearInterval(timerRef.current);
            setElapsedTime('00:00.0');
        }
    }, [liveData.run_start_time]);

    // Auto-stop: when race IS active but candidate queue just emptied
    useEffect(() => {
        const wasPositive = prevCandidateCountRef.current !== null && prevCandidateCountRef.current > 0;
        const nowEmpty = liveData.candidates.length === 0;

        if (wasPositive && nowEmpty && isRaceActiveRef.current && liveData.run_start_time) {
            handleAutoStop();
        }
        prevCandidateCountRef.current = liveData.candidates.length;
    }, [liveData.candidates]);

    const fetchLiveData = async () => {
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/candidates/live-run/${project._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLiveData(resp.data);
        } catch (err) {
            console.error('Failed to fetch live data:', err);
        } finally {
            setInitialLoading(false);
        }
    };

    const updateTimer = () => {
        if (!runStartTimeRef.current) return;
        const start = new Date(runStartTimeRef.current).getTime();
        const now = Date.now();
        const diff = now - start;
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        const millis = Math.floor((diff % 1000) / 100);
        setElapsedTime(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${millis}`);
    };

    // Auto-stop: all candidates passed/failed — trigger silently
    const handleAutoStop = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${CONFIG.API_URL}/api/candidates/stop-run`, {
                projectId: project._id
            }, { headers: { Authorization: `Bearer ${token}` } });

            // Immediately stop timer
            isRaceActiveRef.current = false;
            runStartTimeRef.current = null;
            clearInterval(timerRef.current);
            setElapsedTime('00:00.0');

            // Update local state to reflect stopped race
            setLiveData(prev => ({ ...prev, run_start_time: null }));

            Swal.fire({
                title: '🏁 Race Complete!',
                text: 'All candidates have been processed. Race has been automatically stopped and timer reset.',
                icon: 'success',
                confirmButtonColor: 'var(--primary)',
                confirmButtonText: 'Back to Control'
            }).then(() => {
                if (onBack) onBack();
            });
        } catch (err) {
            console.error('Auto-stop failed:', err);
        }
    };

    // Manual stop via button
    const handleStopRace = async () => {
        const result = await Swal.fire({
            title: 'Stop Race?',
            text: 'This will stop the timer and reset the race state. Are you sure?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#800000',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, Stop Race'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.post(`${CONFIG.API_URL}/api/candidates/stop-run`, {
                    projectId: project._id
                }, { headers: { Authorization: `Bearer ${token}` } });

                // Immediately stop timer locally
                isRaceActiveRef.current = false;
                runStartTimeRef.current = null;
                clearInterval(timerRef.current);
                setElapsedTime('00:00.0');
                setLiveData(prev => ({ ...prev, run_start_time: null }));

                Swal.fire({ title: 'Race Stopped', icon: 'success', timer: 1500, showConfirmButton: false });
                if (onBack) onBack();
            } catch (err) {
                Swal.fire('Error', err.response?.data?.message || 'Failed to stop race', 'error');
            }
        }
    };

    const isRaceCurrentlyActive = !!liveData.run_start_time;
    const top10 = liveData.candidates.slice(0, 10);

    if (initialLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
                <LoadingSpinner message="Connecting to Live Sensors..." />
            </div>
        );
    }

    return (
        <div className="ro-live-container" style={{ display: 'flex', flexDirection: 'column', gap: '24px', paddingBottom: '40px' }}>
            {/* Top Bar: Timer and Controls */}
            <div className="glass-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '28px 32px' }}>
                <div>
                    <h3 className="ro-stat-label" style={{ margin: 0 }}>
                        {isRaceCurrentlyActive ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ width: '10px', height: '10px', background: '#10b981', borderRadius: '50%', display: 'inline-block', animation: 'pulse 1.5s infinite' }}></span>
                                ACTIVE RACE TIMER
                            </span>
                        ) : 'RACE INACTIVE'}
                    </h3>
                    <div className="ro-timer-large">{elapsedTime}</div>
                </div>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div className="ro-stat-card" style={{ padding: '16px 24px', minWidth: '160px', textAlign: 'center' }}>
                        <div className="ro-stat-label">On Track</div>
                        <div className="ro-stat-value">{liveData.candidates.length}</div>
                    </div>
                    <div className="ro-stat-card" style={{ padding: '16px 24px', minWidth: '160px', textAlign: 'center' }}>
                        <div className="ro-stat-label">Completed</div>
                        <div className="ro-stat-value" style={{ color: '#10b981' }}>
                            {liveData.candidates.filter(c => c.laps_completed >= 5).length}
                        </div>
                    </div>

                    {/* Stop Race button — ONLY visible when race is actively running */}
                    {isRaceCurrentlyActive && (
                        <button
                            onClick={handleStopRace}
                            style={{
                                padding: '16px 32px',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '16px',
                                fontWeight: 900,
                                fontSize: '16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 8px 15px rgba(239,68,68,0.3)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                            onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                        >
                            <Icon icon="ion:stop-circle" width="22" height="22" />
                            STOP RACE
                        </button>
                    )}

                    {/* Race inactive indicator */}
                    {!isRaceCurrentlyActive && (
                        <div style={{
                            padding: '16px 24px',
                            background: '#f1f5f9',
                            borderRadius: '16px',
                            color: '#64748b',
                            fontWeight: 800,
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <Icon icon="ion:timer-outline" width="20" />
                            RACE NOT RUNNING
                        </div>
                    )}
                </div>
            </div>

            {/* Main Content: Lap Grid + Leaderboard/Anomaly */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '24px' }}>

                {/* Real-time Lap Grid — fixed height + internal scroll */}
                <div className="ro-table-container" style={{ display: 'flex', flexDirection: 'column', maxHeight: '740px' }}>

                    <div style={{ padding: '20px', background: '#f8fafc', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 900 }}>LIVE LAP PROGRESS</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: isRaceCurrentlyActive ? '#10b981' : '#94a3b8', fontSize: '12px', fontWeight: 800 }}>
                            <div style={{ width: '8px', height: '8px', background: isRaceCurrentlyActive ? '#10b981' : '#94a3b8', borderRadius: '50%', animation: isRaceCurrentlyActive ? 'pulse 1.5s infinite' : 'none' }}></div>
                            {isRaceCurrentlyActive ? 'LIVE STREAM ACTIVE' : 'STREAM INACTIVE'}
                        </div>
                    </div>
                    {/* Scrollable body */}
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                        <table className="ro-table">
                            <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                                <tr>
                                    <th>Chest #</th>
                                    <th>Candidate Name</th>
                                    <th>Laps</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {liveData.candidates.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                                            {isRaceCurrentlyActive
                                                ? <LoadingSpinner message="Waiting for candidates..." />
                                                : 'No race is currently running.'}
                                        </td>
                                    </tr>
                                ) : liveData.candidates.map(c => (
                                    <tr key={c.id}>
                                        <td style={{ color: 'var(--primary)', fontWeight: 800 }}>{c.chest_number}</td>
                                        <td>{c.full_name}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                {[...Array(5)].map((_, i) => (
                                                    <div key={i} style={{
                                                        width: '24px', height: '24px', borderRadius: '6px',
                                                        background: i < c.laps_completed ? '#10b981' : '#f1f5f9',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '10px',
                                                        color: i < c.laps_completed ? 'white' : '#cbd5e1',
                                                        fontWeight: 800
                                                    }}>{i + 1}</div>
                                                ))}
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{ padding: '4px 10px', borderRadius: '8px', background: '#dcfce7', color: '#166534', fontSize: '12px', fontWeight: 700 }}>RUNNING</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Right column: Leaderboard + Anomaly Feed */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Top 10 Leaderboard */}
                    <div className="ro-stat-card" style={{ maxHeight: '320px', display: 'flex', flexDirection: 'column' }}>
                        <h3 className="ro-stat-label">Top 10 Leaderboard</h3>
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {top10.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '13px' }}>Leaderboard will appear once candidates start lapping.</div>
                            ) : top10.map((c, i) => (
                                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                                    <div style={{ width: '28px', height: '28px', background: i < 3 ? 'var(--primary)' : '#f1f5f9', color: i < 3 ? 'white' : 'var(--text-muted)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '13px', flexShrink: 0 }}>
                                        {i + 1}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '14px', fontWeight: 800 }}>{c.full_name}</div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Chest: {c.chest_number}</div>
                                    </div>
                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                        <div style={{ fontSize: '16px', fontWeight: 900, color: 'var(--primary)' }}>{c.laps_completed} ✓</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Anomaly Feed */}
                    <div className="ro-stat-card" style={{ maxHeight: '220px', display: 'flex', flexDirection: 'column' }}>
                        <h3 className="ro-stat-label">Anomaly Alert Feed</h3>
                        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {anomalies.map(a => (
                                <div key={a.id} className="ro-anomaly-item" style={{
                                    borderLeftColor: a.type === 'warning' ? '#f59e0b' : '#3b82f6',
                                    background: a.type === 'warning' ? '#fffbeb' : '#eff6ff'
                                }}>
                                    <div style={{ fontWeight: 800, fontSize: '13px' }}>{a.message}</div>
                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>{a.time} — System Monitor</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LiveRun;
